import Logger from './logger';
import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { degToRad } from 'three/src/math/MathUtils.js';
import { gsap } from 'gsap/all';

class MixtapeList extends HTMLElement {
  #NAME ='MixtapeList';
  #abortController;
  #targetsWrap;
  #targets = [];
  #cassettes = [];
  #checkIfMobile = () => window.innerWidth < 720;
  #tickerCallback = () => this.#render();
  scene;
  renderer;
  camera;
  raycaster;
  pointer;
  intersects;
  width;
  pDiv;
  #currentIndex;
  #currentOverIndex;
  /* #panning = false; */

  #data;
  get data() {
    return this.#data;
  }
  set data(dta) {
    Logger.info(`${this.#NAME} > data > `, dta);
    this.#data = dta;
    this.#buildScene();
  }

  constructor() {
    super();
    Logger.info(`${this.#NAME} > constructor`);
    this.#targetsWrap = this.querySelector('.mixtapes__tape-list__targets-wrap');
  }

  connectedCallback() {
    Logger.info(`${this.#NAME} > connectedCallback`);
  }

  disconnectedCallback() {
    this.#abortController.abort();
    this.#abortController = null;
    gsap.ticker.remove(this.#tickerCallback);
  }

  static get observedAttributes() {
    return ['data-current'];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    Logger.info(`${this.#NAME} > attributeChangedCallback > name: ${name} > newValue: ${newValue}`);
    if (name === 'data-current') {
      this.#currentIndex = parseInt(newValue);
      setTimeout(() => {
        this.style.pointerEvents = 'all';
      }, 2000);
      /* this.#targetsWrap.dataset.index = newValue; */
    }
  }

  #buildScene() {
    Logger.info(`${this.#NAME} > #buildScene`);
    //
    this.scene = new THREE.Scene();
    this.scene.position.x = (this.#data.length * -1.75) / 2;
    this.scene.background = null;
    this.camera = new THREE.PerspectiveCamera( 72, this.clientWidth / this.clientHeight, 0.1, 200 );
    this.camera.position.z = 10;
    this.camera.position.y = 7;
    //
    this.renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true, preserveDrawingBuffer: true });
    /* this.renderer.setPixelRatio( window.devicePixelRatio ); */
    this.renderer.setSize( this.clientWidth, this.clientHeight );
    this.prepend( this.renderer.domElement );
    //
    const light = new THREE.AmbientLight( 0xFFFFFF, 1 ); // soft white light
    this.scene.add( light );
    //
    const dirLight = new THREE.DirectionalLight( 0xFFFFFF, 1.5 );
    dirLight.position.set(0, 1, 30);
    this.scene.add( dirLight );
    //
    const loader = new GLTFLoader();
    loader.load( 
      '/wp-content/themes/wonderpress/static/dist/images/cassette_in_case_2.gltf', 
      (e) => this.#onLoadGLTF(e), 
      null, 
      (e) => this.#onLoadGTLFError(e)
    );
  }

  #onLoadGLTF( gltf ) {
    Logger.info(`${this.#NAME} > onLoadGTLF`);
    console.log(gltf);
    const startingLeft = this.#data.length === 12 ? 28 : (this.clientWidth/2) - ( (this.clientWidth * 0.0647) * (this.#data.length / 2));
    gltf.scene.rotation.set(0,degToRad(90),0);
    gsap.set(this.#targetsWrap, {
      x: `${100 - (this.#data.length * 7.55)}%`,
    });
    //
    this.#cassettes =[];
    let bitmapLoader = new THREE.ImageBitmapLoader();
    let numLoaded = 0
    //
    this.#data.forEach((obj, index, arr) => {
      const cassette = gltf.scene.clone();
      let origMaterial;
      cassette.position.set((1.9 * index),0,0);
      cassette.name = 'cassette';
      cassette.userData = {
        title: obj.title,
        index,
        x: cassette.position.x,
        y: cassette.position.y,
        mobileZ: -0.6,
        rotationY: cassette.rotation.y,
      }
      /* console.log(cassette.userData); */
      this.#targets.push(this.#createTarget(index, startingLeft, obj.number, obj.title));
      /* console.log(cassette) */
      this.#cassettes.push(cassette);
  
      cassette.traverse(child => {
        if (child.material && child.material.name === 'Case_paper') {
          console.log(child.material);
          bitmapLoader.load(
            obj.card, 
            (imageBitmap) => {
              const texture = new THREE.CanvasTexture( imageBitmap );
              texture.flipY = false;
              texture.colorSpace = 'srgb';
              texture.wrapS = 1000;
              texture.wrapT = 1000;
              //
              if(!origMaterial) origMaterial = child.material.clone();
              origMaterial.map = texture;
              child.material = origMaterial;
              numLoaded++
              if(numLoaded === arr.length) this.#onSceneReady();
            },
            undefined,
            (err) => {
              console.log(err);
            }
        );
        }
      });
      //
      this.scene.add( cassette );
    });
    //
    if (this.#checkIfMobile()) {
      this.#setMobile();
    }
  }

  #onLoadGTLFError(error) {
    Logger.info(`${this.#NAME} > #onLoadGTLFError > ${error}`);
  }

  #createTarget(index, startingLeft, number, title) {
    const target = document.createElement('DIV');
    const left = ((startingLeft + ( (this.clientWidth * 0.065) * (index))) / this.clientWidth) * 100;
    target.className = 'target';
    target.dataset.index = index;
    target._left = left;
    target.style.left = `${left}%`;
    //
    const info = document.createElement('DIV');
    info.className = 'target__info';
    //
    const num = document.createElement('DIV');
    num.className = 'target__info__number';
    num.textContent = number;
    //
    const ttle = document.createElement('DIV');
    ttle.className = 'target__info__title';
    ttle.textContent = title;
    //
    info.append(num, ttle);
    target.append(info);
    return target;
  }

  #onSceneReady() {
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    //
    this.#targetsWrap.append(...this.#targets);
    this.#targets.forEach((el) => {
      const index = parseInt(el.dataset.index);
      el.addEventListener('click', () => {
        this.#onOver(index, true);
      }, { signal });
      //
      el.addEventListener('mousemove', () => this.#onOver(index), { signal });
      el.addEventListener('mouseout', () => this.#onOut(), { signal });
    });
    //
    this.addEventListener('pointerleave', () => {
      this.#currentOverIndex = null;
      this.dataset.overIndex = '';
      this.#onOut();
    }, { signal });
    //
    this.dispatchEvent(new Event('mixtape:list:ready', { bubbles: true }));
    this.#render();
    /* gsap.ticker.add(this.#tickerCallback); */
    if (this.#currentIndex !== undefined) this.#onOver(this.#currentIndex);
  }

  #render() {
    this.renderer.render( this.scene, this.camera );
  }

  #onOverMobile(index, tl) {
    tl.addLabel('start', 0);
    //
    this.#cassettes.forEach((c,i) => {
      if (i === index) {
        //
        tl.add(gsap.to(c.position, {
          z: 0.2,
          duration: 0.3,
          ease: 'power2.out',
          overwrite: true
        }), 'start');
        //
      } else {
        tl.add(gsap.to(c.position, {
          z: c.userData.mobileZ,
          duration: 0.3,
          ease: 'power2.out',
          overwrite: true
        }), 'start');
      }
    });
  }

  #onOverDesktop(index, tl) {
    tl.addLabel('start', 0)
      .addLabel('position', 0.2)
      .addLabel('reveal', 0.35);
    if (this.scene.position.x !== (this.#data.length * -2.2) / 2) {
      tl.add(gsap.to(this.scene.position, {
        x: (this.#data.length * -2.2) / 2,
        duration: 0.3,
        ease: 'power2.out',
        overwrite: true
      }), 'start');
      //
      tl.add(gsap.to(this.#targetsWrap, {
        x: `0%`,
        duration: 0.3,
        ease: 'power2.out',
        overwrite: true
      }), 'start');

    }
    //
    this.#cassettes.forEach((c,i) => {
      if (i === index) {
        //
        tl.add(gsap.to(c.rotation, {
          y: 0,
          duration: 0.3,
          ease: 'power2.out',
          overwrite: true
        }), 'reveal');
        //
        tl.add(gsap.to(c.position, {
          x: c.userData.x - (0.1 * index),
          y: c.userData.y + 2.9,
          duration: 0.3,
          ease: 'power2.out',
          overwrite: true
        }), 'reveal');
        //
        tl.add(gsap.to(this.#targets[i], {
          width: '26.25%',
          left: `${this.#targets[i]._left}%`,
          /* x: `${-2 + (0.5 * i)}%`, */
          zIndex: 2,
          duration: 0.3,
          ease: 'power2.out',
          overwrite: true
        }), 'reveal');
        //
        tl.add(gsap.to(this.#targets[i].firstElementChild, {
          opacity: 1,
          duration: 0.2,
          ease: 'power2.out',
          overwrite: true
        }), 'reveal+=0.1');
        //
      } else if (i > index) {
        //
        tl.add(gsap.to(c.position, {
          x: c.userData.x + 5.75,
          y: c.userData.y,
          duration: 0.3,
          ease: 'power2.out',
          overwrite: true
        }), 'position');
        //
        tl.add(gsap.to(this.#targets[i], {
          width: '6.5%',
          left: `${this.#targets[i]._left + 26 - 6.5}%`,
          x: '0%',
          zIndex: 1,
          duration: 0.3,
          ease: 'power2.out',
          overwrite: true
        }), 'position');
        //
      } else if (i < index) {
        tl.add(gsap.to(c.position, {
          x: c.userData.x,
          y: c.userData.y,
          duration: 0.3,
          ease: 'power2.out',
          overwrite: true
        }), 'position');
        //
        tl.add(gsap.to(this.#targets[i], {
          width: '6.5%',
          left: `${this.#targets[i]._left}%`,
          x: '0%',
          zIndex: 1,
          duration: 0.3,
          ease: 'power2.out',
          overwrite: true
        }), 'position');
      }
      //
      if (i !== index)  {
        tl.add(gsap.to(c.rotation, {
          y: c.userData.rotationY,
          duration: 0.3,
          ease: 'power2.in',
          overwrite: true
        }), 'start');
        //
        tl.add(gsap.to(this.#targets[i].firstElementChild, {
          opacity: 0,
          duration: 0.2,
          ease: 'power2.in',
          overwrite: true
        }), 'start');
      }
    });
  }

  #onOver(index, click = false) {
    this.#currentOverIndex = index;
    this.dataset.overIndex = index;
    const tl = gsap.timeline({ 
      smoothChildTiming: true,
      onStart: () => this.renderer.setAnimationLoop( () => this.#render() ),
      onComplete: () => {
        this.#currentOverIndex = null;
        this.renderer.setAnimationLoop( null );
        if (click) {
          this.style.pointerEvents = 'none';
          this.dispatchEvent( new CustomEvent('mixtape:switch', { 
            bubbles: true, 
            detail: { index } 
          }));
        }
      }
    });
    //
    if (this.#checkIfMobile()) {
      this.#onOverMobile(index,tl);
    } else {
      this.#onOverDesktop(index,tl);
    }
  }

  #onOut() {
    if (this.#checkIfMobile()) return;
    const tl = gsap.timeline({ 
      smoothChildTiming: true, 
      onStart: () => this.renderer.setAnimationLoop( () => this.#render() ),
      onComplete: () => {
        this.renderer.setAnimationLoop( null );
        if (!this.#currentOverIndex && this.#currentIndex !== undefined) {
          this.#onOver(this.#currentIndex);
        } 
      }
    })
      .addLabel('start', 0)
      .addLabel('position', 0.2);
    //
    if (!this.#currentIndex) {
      tl.add(gsap.to(this.scene.position, {
        x: (this.#data.length * -1.75) / 2,
        duration: 0.3,
        ease: 'power2.in',
        overwrite: true
      }), 'start');
      //
      tl.add(gsap.to(this.#targetsWrap, {
        x: `${100 - (this.#data.length * 7.55)}%`,
        duration: 0.3,
        ease: 'power2.out',
        overwrite: true
      }), 'start');
    }
    this.#cassettes.forEach((c,i) => {
      tl.add(gsap.to(c.position, {
        x: c.userData.x,
        y: c.userData.y,
        duration: 0.3,
        ease: 'power2.in',
        overwrite: true
      }), 'position');
      //
      tl.add(gsap.to(c.rotation, {
        y: c.userData.rotationY,
        duration: 0.3,
        ease: 'power2.in',
        overwrite: true
      }), 'start');
      //
      tl.add(gsap.to(this.#targets[i], {
        width: '6.5%',
        left: `${this.#targets[i]._left}%`,
        x: '0%',
        zIndex: 1,
        duration: 0.3,
        ease: 'power2.in',
        overwrite: true
      }), 'position');
      //
      tl.add(gsap.to(this.#targets[i].firstElementChild, {
        opacity: 0,
        duration: 0.2,
        ease: 'power2.in',
        overwrite: true
      }), 'position-=0.1');
    });
  }

  onResize() {
    Logger.info(`${this.#NAME} > onResize`);
    if (this.camera && this.width != this.clientWidth) {
      this.camera.aspect = this.clientWidth / this.clientHeight;
      this.camera.updateProjectionMatrix();
      this.renderer.setSize( this.clientWidth, this.clientHeight );
      this.width = this.clientWidth;
      if (this.#checkIfMobile()) {
        this.#setMobile();
      } else {
        this.#cassettes.forEach((cassette,i) => {
          cassette.position.z = 0;
        });
        this.#onOver(this.#currentIndex);
      }
      this.#render();
    }
  }

  #setMobile() {
    gsap.set(this.#targetsWrap, {
      x: `-0.5%`,
      scale: 0.95
      /* z: '-10px' */
    });
    this.#targets.forEach((t, i) => {
      gsap.set(t, {
        width: '7.9%',
        left: `${t._left + (i * 1.4)}%`,
        x: '0%',
        zIndex: 1,
        overwrite: true
      });
      gsap.set(t.firstElementChild, {
        opacity: 0,
        overwrite: true
      });
    })
    this.scene.position.x = this.#cassettes.length * -0.9;
    this.#cassettes.forEach((c) => {
      gsap.set(c.position, {
        x: c.userData.x,
        y: c.userData.y,
        z: c.userData.mobileZ,
        overwrite: true
      });
      gsap.set(c.rotation, {
        y: c.userData.rotationY,
        overwrite: true
      });
    });
  }

}

customElements.define('mixtape-list', MixtapeList);